import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

/**
 * Centralized token acquisition. The app uses native auth (/auth/login).
 *
 * Flow:
 * 1. Try acquireTokenSilent() — returns cached or silently-refreshed token
 * 2. On any failure → clear all auth state (localStorage + sessionStorage)
 *    and redirect to /auth/login so the login form actually shows
 *    (without clearing state, LoginPage auto-redirects back because the
 *    native auth session still appears active)
 */
export async function getAuthToken(instance: PublicClientApplication): Promise<string | null> {
  try {
    const accounts = instance.getAllAccounts();

    if (!accounts || accounts.length === 0) {
      console.warn('[getAuthToken] No accounts found, clearing state and redirecting to login');
      forceLogout();
      return null;
    }

    const account = accounts[0];

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      return response.idToken;
    } catch (silentError: any) {
      console.warn('[getAuthToken] Silent token acquisition failed, forcing logout:', silentError.errorCode);
      forceLogout();
      return null;
    }
  } catch (error) {
    console.error('[getAuthToken] Unexpected error:', error);
    forceLogout();
    return null;
  }
}

/**
 * Clears all auth state (mirrors LogoutButton native auth path) then
 * redirects to the login page so it shows the login form, not auto-redirect.
 */
function forceLogout(): void {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/auth/login';
}
